import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  CheckCircle, Users, Calendar, FileText, DollarSign, Clock, 
  Shield, Zap, BarChart3, Send, ArrowRight, Star, Quote,
  Briefcase, Calculator, Receipt, Phone, Mail, Building2, Loader2,
  Menu, X
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../lib/api';

function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref');
  
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company_name: '',
    phone: '',
    trade: '',
    message: ''
  });

  // Auto-open form if there's a referral code
  useEffect(() => {
    if (referralCode) {
      // Small delay for better UX
      setTimeout(() => setShowRequestForm(true), 1000);
    }
  }, [referralCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Auto-detect the requester's IANA timezone (e.g. "America/Phoenix",
      // "America/New_York"). Carried into the company record on approval so
      // schedulers and Reminder labels run in the tenant's local clock.
      const browserTz = (() => {
        try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; }
        catch { return null; }
      })();
      await apiClient.post('/auth/request-account', {
        ...formData,
        referral_code: referralCode || null,
        timezone: browserTz,
      });
      toast.success("Request submitted! We'll be in touch soon.");
      setShowRequestForm(false);
      setFormData({ name: '', email: '', company_name: '', phone: '', trade: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    {
      icon: Calendar,
      title: "Smart Scheduling",
      description: "Drag-and-drop calendar with crew assignments, route optimization, and customer notifications."
    },
    {
      icon: Calculator,
      title: "Profitable Estimates",
      description: "Build accurate estimates with labor, materials, and overhead costs. Know your profit before you start."
    },
    {
      icon: FileText,
      title: "Digital Contracts",
      description: "Professional proposals with e-signatures. Customers approve and sign from any device."
    },
    {
      icon: Receipt,
      title: "Invoicing & Payments",
      description: "Send invoices, collect deposits, and get paid faster with integrated payment processing."
    },
    {
      icon: Clock,
      title: "Time Tracking",
      description: "Track crew hours by job. Know exactly where your labor costs are going."
    },
    {
      icon: Users,
      title: "Team Management",
      description: "Manage employees, assign jobs, track performance, and handle payroll integration."
    },
    {
      icon: BarChart3,
      title: "Business Insights",
      description: "Real-time dashboards showing revenue, job profitability, and growth trends."
    },
    {
      icon: Shield,
      title: "Bank-Grade Security",
      description: "Encrypted at rest and in transit. Role-based access keeps sensitive job costs where they belong."
    }
  ];

  const testimonials = [
    {
      name: "Mike Johnson",
      company: "Johnson Landscaping",
      quote: "CrewSync Pro cut our admin time in half. We went from spreadsheets to a real system in one day.",
      rating: 5
    },
    {
      name: "Sarah Williams",
      company: "Elite Concrete",
      quote: "The estimating tool alone pays for itself. I know my profit margin before I even send the quote.",
      rating: 5
    },
    {
      name: "David Chen",
      company: "Chen's Painting Co.",
      quote: "My customers love the professional proposals. We've increased our close rate by 40%.",
      rating: 5
    }
  ];

  const trades = [
    "Landscaping",
    "Concrete/Coatings",
    "Painting",
    "Roofing",
    "HVAC",
    "Plumbing",
    "Electrical",
    "General Contracting",
    "Fencing",
    "Pool Services",
    "Cleaning Services",
    "Other"
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/90 backdrop-blur-lg border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_crewsync-business/artifacts/srw9khxz_CrewSync%20Pro%20Logo.png" 
                alt="CrewSync Pro" 
                className="h-8 w-auto"
              />
              <span className="text-xl font-bold hidden sm:inline">CrewSync Pro</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <a href="#features" className="text-zinc-400 hover:text-white transition-colors">Features</a>
              <a href="#testimonials" className="text-zinc-400 hover:text-white transition-colors">Testimonials</a>
              <a href="#pricing" className="text-zinc-400 hover:text-white transition-colors">Pricing</a>
              <Button variant="outline" onClick={() => navigate('/login')} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                Sign In
              </Button>
              <Button onClick={() => setShowRequestForm(true)} className="bg-primary hover:bg-primary/90 text-black">
                Get Started
              </Button>
            </div>
            
            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2 text-zinc-400 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
          
          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-zinc-800">
              <div className="flex flex-col gap-4">
                <a href="#features" className="text-zinc-400 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>Features</a>
                <a href="#testimonials" className="text-zinc-400 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>Testimonials</a>
                <a href="#pricing" className="text-zinc-400 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
                <Button variant="outline" onClick={() => navigate('/login')} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                  Sign In
                </Button>
                <Button onClick={() => { setShowRequestForm(true); setMobileMenuOpen(false); }} className="bg-primary hover:bg-primary/90 text-black">
                  Get Started
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            {referralCode && (
              <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-full px-4 py-2 mb-6 text-primary text-sm">
                <Star className="w-4 h-4" />
                You were referred by a CrewSync Pro user!
              </div>
            )}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Run Your Field Service Business{' '}
              <span className="text-primary">Like a Pro</span>
            </h1>
            <p className="text-xl text-zinc-400 mb-8 max-w-2xl mx-auto">
              All-in-one platform for contractors: scheduling, estimates, invoicing, 
              time tracking, and team management. Built by contractors, for contractors.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={() => setShowRequestForm(true)}
                className="bg-primary hover:bg-primary/90 text-black text-lg px-8 py-6"
              >
                Start Free Trial <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-lg px-8 py-6"
              >
                See Features
              </Button>
            </div>
            <p className="mt-6 text-sm text-zinc-500">
              14-day free trial. No credit card required.
            </p>
          </div>
          
          {/* Hero Image/Dashboard Preview */}
          <div className="mt-16 relative">
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent z-10 pointer-events-none"></div>
            <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-4 shadow-2xl">
              <div className="aspect-video bg-zinc-800 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <BarChart3 className="w-16 h-16 text-primary mx-auto mb-4" />
                  <p className="text-zinc-400">Dashboard Preview</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Everything You Need to Grow</h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Stop juggling spreadsheets, texts, and paper. Get one powerful platform that handles it all.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="bg-zinc-900 border-zinc-800 hover:border-primary/50 transition-colors">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-white">{feature.title}</h3>
                  <p className="text-zinc-400 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Trusted by Contractors</h2>
            <p className="text-zinc-400 text-lg">See what other business owners are saying</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-primary text-primary" />
                    ))}
                  </div>
                  <Quote className="w-8 h-8 text-primary/30 mb-2" />
                  <p className="text-zinc-300 mb-4 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-semibold text-white">{testimonial.name}</p>
                    <p className="text-sm text-zinc-500">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Simple, Transparent Pricing</h2>
            <p className="text-zinc-400 text-lg">Start free, upgrade when you're ready</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Starter Plan */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Starter</CardTitle>
                <CardDescription className="text-zinc-400">For solo contractors</CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-white">$49</span>
                  <span className="text-zinc-400">/month</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {['Up to 50 jobs/month', 'Basic estimates', 'Invoicing', 'Calendar', '1 user'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-zinc-300">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Button 
                  variant="outline" 
                  className="w-full border-zinc-700 hover:bg-zinc-800"
                  onClick={() => setShowRequestForm(true)}
                >
                  Start Free Trial
                </Button>
              </CardContent>
            </Card>
            
            {/* Pro Plan */}
            <Card className="bg-zinc-900 border-primary relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-black text-xs font-bold px-3 py-1 rounded-full">
                MOST POPULAR
              </div>
              <CardHeader>
                <CardTitle className="text-white">Pro</CardTitle>
                <CardDescription className="text-zinc-400">For growing teams</CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-white">$99</span>
                  <span className="text-zinc-400">/month</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {['Unlimited jobs', 'Profitable estimates', 'Time tracking', 'Team management', 'Up to 10 users', 'Priority support'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-zinc-300">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Button 
                  className="w-full bg-primary hover:bg-primary/90 text-black"
                  onClick={() => setShowRequestForm(true)}
                >
                  Start Free Trial
                </Button>
              </CardContent>
            </Card>
            
            {/* Enterprise Plan */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Enterprise</CardTitle>
                <CardDescription className="text-zinc-400">For large operations</CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-white">Custom</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {['Everything in Pro', 'White-label branding', 'Custom integrations', 'Dedicated support', 'Unlimited users', 'API access'].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-zinc-300">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Button 
                  variant="outline" 
                  className="w-full border-zinc-700 hover:bg-zinc-800"
                  onClick={() => setShowRequestForm(true)}
                >
                  Contact Sales
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to Take Control of Your Business?
          </h2>
          <p className="text-zinc-400 text-lg mb-8">
            Join hundreds of contractors who are saving time and making more money with CrewSync Pro.
          </p>
          <Button 
            size="lg" 
            onClick={() => setShowRequestForm(true)}
            className="bg-primary hover:bg-primary/90 text-black text-lg px-8 py-6"
          >
            Get Started Today <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_crewsync-business/artifacts/srw9khxz_CrewSync%20Pro%20Logo.png" 
                alt="CrewSync Pro" 
                className="h-6 w-auto"
              />
              <span className="text-zinc-400">CrewSync Pro</span>
            </div>
            <div className="flex gap-6 text-sm text-zinc-500">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-white transition-colors">Contact</a>
            </div>
            <p className="text-sm text-zinc-500">
              © {new Date().getFullYear()} CrewSync Pro. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Request Account Dialog */}
      <Dialog open={showRequestForm} onOpenChange={setShowRequestForm}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              Get Started with CrewSync Pro
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Fill out the form below and we'll set up your account within 24 hours.
              {referralCode && (
                <span className="block mt-2 text-primary">
                  You were referred by a friend! 
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Your Name *</Label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="John Smith"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Email *</Label>
                <Input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@company.com"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Company Name *</Label>
              <Input
                required
                value={formData.company_name}
                onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                placeholder="Smith Landscaping LLC"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Phone</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="(555) 123-4567"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Industry</Label>
                <Select 
                  value={formData.trade} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, trade: value }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {trades.map(trade => (
                      <SelectItem key={trade} value={trade} className="text-white hover:bg-zinc-700">
                        {trade}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Tell us about your business (optional)</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                placeholder="How many employees? What services do you offer?"
                rows={3}
                className="bg-zinc-800 border-zinc-700 text-white resize-none"
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={submitting}
              className="w-full bg-primary hover:bg-primary/90 text-black"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Request Access
                </>
              )}
            </Button>
            
            <p className="text-xs text-zinc-500 text-center">
              By submitting, you agree to our Terms of Service and Privacy Policy.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default LandingPage;
